<template>
  <div class="banner-carousel-section">
    <div class="banner-carousel-wrapper">
      <carousel 
        :perPage="1" 
        :autoplay="autoplay" 
        :loop="loop" 
        :paginationEnabled="showPagination"
        :paginationPadding="5"
        :paginationSize="8"
        :paginationActiveColor="paginationActiveColor"
        :paginationColor="paginationColor"
        :autoplayTimeout="autoplayTimeout"
        class="custom-carousel"
      >
        <!-- Placeholder slide when no items -->
        <slide v-if="bannerItems.length === 0">
          <div class="placeholder-slide">
            <i class="mdi mdi-image-area" style="font-size: 4rem; color: #ccc;"></i>
            <p v-if="placeholderText" class="placeholder-text">{{ placeholderText }}</p>
          </div>
        </slide>
        
        <!-- Banner slides -->
        <slide v-for="(item, index) in bannerItems" :key="index">
          <a 
            :href="item.link" 
            class="banner-link"
            @click="handleBannerClick(item, $event)"
          >
            <img 
              :src="item.image" 
              :alt="item.title || `Banner ${index + 1}`" 
              class="banner-image" 
              @error="handleImageError"
            />
          </a>
        </slide>
      </carousel>
    </div>
  </div>
</template>

<script>
import { Carousel, Slide } from 'vue-carousel';

export default {
  name: "AppBannerCarousel",
  components: {
    Carousel,
    Slide
  },
  props: {
    bannerItems: {
      type: Array,
      default: () => []
    },
    autoplay: {
      type: Boolean,
      default: true
    },
    loop: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    autoplayTimeout: {
      type: Number,
      default: 5000
    },
    paginationActiveColor: {
      type: String,
      default: "#F2994A"
    },
    paginationColor: {
      type: String,
      default: "#D1D5DB"
    },
    height: {
      type: String,
      default: "300px"
    },
    mobileHeight: {
      type: String,
      default: "180px"
    },
    placeholderText: {
      type: String,
      default: "No banners available"
    },
    clickable: {
      type: Boolean,
      default: true
    },
    // bannerLocation: {
    //   type: String,
    //   default: ""
    // },
  },
  methods: {
    handleBannerClick(item, event) {
      if (!this.clickable) {
        event.preventDefault();
        return;
      }
      
      this.$emit('banner-click', item);
      
      // If no custom handler and item has link, allow default behavior
      if (!this.$listeners['banner-click'] && item.link) {
        // Default behavior will proceed
        return;
      }
      
      // Prevent default if custom handler exists
      if (this.$listeners['banner-click']) {
        event.preventDefault();
      }
    },
    handleImageError(event) {
      // Replace broken image with placeholder
      event.target.style.display = 'none';
      const placeholder = event.target.parentNode.querySelector('.error-placeholder') || 
                        document.createElement('div');
      placeholder.className = 'error-placeholder';
      placeholder.innerHTML = '<i class="mdi mdi-image-broken" style="font-size: 3rem; color: #ccc;"></i>';
      event.target.parentNode.appendChild(placeholder);
    },
  }
};
</script>

<style scoped>
/* ========================================= */
/* CAROUSEL CONTAINER                         */
/* ========================================= */
.banner-carousel-section {
  margin-bottom: 2rem;
  max-width: 1100px;
  margin-left: 32px;
  margin-right: 32px;
}

.banner-carousel-wrapper {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  position: relative;
  padding: 24px;
}

/* ========================================= */
/* CAROUSEL SLIDES                            */
/* ========================================= */
.banner-link {
  display: block;
  width: 100%;
  text-decoration: none;
}

.banner-image,
.placeholder-slide {
  width: 100%;
  height: v-bind(height);
  object-fit: fill;
  border-radius: 8px;
  background-color: #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transition: transform 0.3s ease;
}

.banner-image:hover {
  transform: scale(1.02);
}

.placeholder-text {
  margin-top: 1rem;
  color: #9CA3AF;
  font-size: 1rem;
  font-weight: 500;
}

.error-placeholder {
  width: 100%;
  height: v-bind(height);
  background-color: #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

/* ========================================= */
/* CAROUSEL PAGINATION STYLING               */
/* ========================================= */
.custom-carousel >>> .VueCarousel-pagination {
  position: absolute;
  bottom: 35px;
  right: 35px;
  width: auto !important;
  background: rgba(243, 244, 246, 0.8);
  border-radius: 20px;
  padding: 0px 10px;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 5px;
  backdrop-filter: blur(10px);
}

.custom-carousel >>> .VueCarousel-dot-container {
  margin: 0px !important;
}

.custom-carousel >>> .VueCarousel-dot-container >>> button {
  margin: 0px !important;
}

.custom-carousel >>> .VueCarousel-dot {
  margin: 0px !important;
  transition: all 0.3s ease;
}

.custom-carousel >>> .VueCarousel-dot:hover {
  transform: scale(1.2);
}

/* ========================================= */
/* MOBILE RESPONSIVE STYLES                  */
/* ========================================= */
@media (max-width: 1024px) {
  .banner-carousel-section {
    padding: 24px 32px;
    margin: 0;
  }
}

@media (max-width: 768px) {
  .banner-carousel-section {
    max-width: 350px;
    width: 100%;
    margin: 0 auto 1.5rem auto;
  }

  .banner-carousel-wrapper {
    padding: 12px;
    border-radius: 12px;
  }
}
</style>